import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Prompt } from "react-router";
import { Button, Select, Space } from "antd";
import { compact, every, filter, find, flatMap, isNil, map, some, trim, uniq } from "lodash";
import { LEAVE_CONFIRMATION } from "constants/common";
import { aiccTtsSettingApi } from "services/aiccLearningAssets";
import { AiccTtsTarget, AiccTtsVoice, AiccTtsVoiceRow } from "types/model/aiccLearningAssets";
import AdjustBaseSpeedModal from "./AdjustBaseSpeedModal";
import getColumns from "./columnConfig";
import {
  isVoiceIdNotFound,
  showTtsModel,
  TTS_ACTIVE_MODEL_LABEL,
  TTS_FIELD_GUIDE,
  TTS_FIELD_GUIDE_INTRO,
  TTS_MODEL_NOT_SELECTABLE,
  TTS_VOICES_TITLE,
} from "./configs";
import styles from "./TtsSetting.module.scss";
import LearningAssetPage from "../LearningAssetPage";
import learningAssetStyles from "../LearningAssets.module.scss";
import { useLearningAssetVersions } from "../hooks/useLearningAssetVersions";
import { useAiccTtsVoices } from "../hooks/useAiccTtsVoices";
import { useAiccTtsVoiceResolutions } from "../hooks/useAiccTtsVoiceResolutions";

interface AdjustingVoice {
  gender: string;
  target: AiccTtsTarget;
}

function TtsSetting() {
  const state = useLearningAssetVersions(aiccTtsSettingApi.fetchVersions);
  const { items, languageId, groupStatus, isEditable, isSaving, runWrite, ttsModel, ttsModelOptions } = state;
  const { voicesById, isLoading: isVoicesLoading } = useAiccTtsVoices();

  const [draftRows, setDraftRows] = useState<AiccTtsVoiceRow[]>();
  // The model selection being edited, saved with the voices in the same PUT.
  const [draftModel, setDraftModel] = useState<string>();
  const [showErrors, setShowErrors] = useState(false);
  const [adjustingVoice, setAdjustingVoice] = useState<AdjustingVoice>();

  const isEditing = !isNil(draftRows);
  const rows = draftRows ?? items;
  const shownModel = isEditing ? draftModel : ttsModel;
  const shownOption = find(ttsModelOptions, { id: shownModel });
  const modelLabel = showTtsModel(shownModel);
  const isModelSelectable = !!find(ttsModelOptions, { id: ttsModel });

  const cancelEdit = useCallback(() => {
    setDraftRows(undefined);
    setDraftModel(undefined);
    setShowErrors(false);
    setAdjustingVoice(undefined);
  }, []);

  useEffect(() => {
    cancelEdit();
  }, [cancelEdit, groupStatus, languageId]);

  const draftVoiceIds = useMemo(
    () => uniq(compact(flatMap(draftRows, ({ main, fallback }) => [trim(main.voiceId), trim(fallback.voiceId)]))),
    [draftRows]
  );

  // Only ids the account's voice list cannot answer for are asked about, and only once that list is in.
  const resolutions = useAiccTtsVoiceResolutions(
    useMemo(() => (isVoicesLoading ? [] : filter(draftVoiceIds, (voiceId) => !voicesById[voiceId])), [
      draftVoiceIds,
      isVoicesLoading,
      voicesById,
    ])
  );

  const hasNotFoundVoice = useMemo(
    () => some(draftVoiceIds, (voiceId) => isVoiceIdNotFound(voiceId, voicesById, resolutions)),
    [draftVoiceIds, resolutions, voicesById]
  );

  const isDraftComplete = useMemo(
    () =>
      every(draftRows, ({ main, fallback }) =>
        every([main, fallback], ({ voiceId, baseSpeed }) => !!trim(voiceId) && !isNil(baseSpeed))
      ),
    [draftRows]
  );

  const hasSameVoiceRow = useMemo(
    () =>
      some(draftRows, ({ main, fallback }) => !!trim(main.voiceId) && trim(main.voiceId) === trim(fallback.voiceId)),
    [draftRows]
  );

  const saveEdit = useCallback(() => {
    if (isNil(draftRows)) {
      return;
    }
    if (!isDraftComplete || hasSameVoiceRow || hasNotFoundVoice) {
      setShowErrors(true);
      return;
    }
    runWrite(
      (id) => aiccTtsSettingApi.update(id, draftRows, draftModel, ttsModel),
      `Updated ${TTS_VOICES_TITLE} successfully.`,
      cancelEdit
    );
  }, [cancelEdit, draftModel, draftRows, hasNotFoundVoice, hasSameVoiceRow, isDraftComplete, runWrite, ttsModel]);

  const updateVoice = useCallback((gender: string, target: AiccTtsTarget, voice: Partial<AiccTtsVoice>) => {
    setDraftRows((currentRows) =>
      map(currentRows, (row) => (row.id === gender ? { ...row, [target]: { ...row[target], ...voice } } : row))
    );
  }, []);

  const columns = useMemo(
    () =>
      getColumns({
        isEditing,
        showErrors,
        voicesById,
        resolutions,
        onVoiceIdChange: (gender, target, voiceId) => updateVoice(gender, target, { voiceId }),
        onAdjust: (gender, target) => setAdjustingVoice({ gender, target }),
      }),
    [isEditing, resolutions, showErrors, updateVoice, voicesById]
  );

  const adjustingRow = adjustingVoice && find(rows, { id: adjustingVoice.gender });
  const adjustingTargetVoice = adjustingRow && adjustingVoice && adjustingRow[adjustingVoice.target];

  return (
    <>
      <Prompt when={isEditing || isSaving} message={LEAVE_CONFIRMATION} />
      <LearningAssetPage<AiccTtsVoiceRow>
        title="TTS Setting"
        fieldGuideIntro={TTS_FIELD_GUIDE_INTRO}
        fieldGuide={TTS_FIELD_GUIDE}
        columns={columns}
        state={{ ...state, items: rows }}
        isEditing={isEditing}
        actions={
          <div className={learningAssetStyles.tableHeader}>
            <div className={learningAssetStyles.sectionHeader}>
              <div className={styles.activeModel}>
                <span className={learningAssetStyles.sectionTitle}>{`${TTS_ACTIVE_MODEL_LABEL}: `}</span>
                {isEditing && ttsModelOptions.length > 0 ? (
                  <Select
                    className={styles.modelSelect}
                    value={draftModel}
                    disabled={isSaving}
                    onChange={(value: string) => setDraftModel(value)}
                    options={map(ttsModelOptions, ({ id }) => ({ value: id, label: showTtsModel(id) }))}
                  />
                ) : (
                  <span className={learningAssetStyles.sectionTitle}>{modelLabel}</span>
                )}
              </div>
              {isEditable &&
                (isEditing ? (
                  <Space size={12}>
                    <Button shape="round" disabled={isSaving} onClick={cancelEdit}>
                      Cancel
                    </Button>
                    <Button type="primary" shape="round" disabled={isSaving} onClick={saveEdit}>
                      Save
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    shape="round"
                    disabled={isNil(languageId)}
                    onClick={() => {
                      setDraftRows(items);
                      setDraftModel(ttsModel);
                    }}
                  >
                    Edit
                  </Button>
                ))}
            </div>
            {isEditing && !isNil(ttsModel) && !isModelSelectable && (
              <div className={styles.modelHint}>{`${showTtsModel(ttsModel)}: ${TTS_MODEL_NOT_SELECTABLE}`}</div>
            )}
            <div className={learningAssetStyles.sectionHeader}>
              <div className={learningAssetStyles.sectionTitle}>{TTS_VOICES_TITLE}</div>
            </div>
          </div>
        }
      >
        {adjustingVoice && adjustingTargetVoice && (
          <AdjustBaseSpeedModal
            languageId={languageId}
            voiceId={adjustingTargetVoice.voiceId ?? ""}
            target={adjustingVoice.target}
            model={shownModel}
            modelLabel={modelLabel}
            streaming={ttsModelOptions.length > 0 ? !!shownOption?.supportsStreamPlay : undefined}
            currentBaseSpeed={adjustingTargetVoice.baseSpeed}
            onCancel={() => setAdjustingVoice(undefined)}
            onSave={(baseSpeed) => {
              updateVoice(adjustingVoice.gender, adjustingVoice.target, { baseSpeed });
              setAdjustingVoice(undefined);
            }}
          />
        )}
      </LearningAssetPage>
    </>
  );
}

export default TtsSetting;
